"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import ActionUploadModal from "@/components/action-upload-modal";
import PageLoadingState from "@/components/page-loading-state";
import {
  getFlowState,
  isPersistedSystemId,
  isTemporarySystemId,
  updateFlowState,
} from "@/lib/connector-flow-state";
import {
  fetchActionsBySystemId,
  previewActionsFromContent,
} from "@/services/action-service";
import { createSystem, deleteSystem } from "@/services/system-service";
import { useSetSystemStatus } from "@/services/hooks/use-systems";

type AddConnectorApiStepProps = {
  systemId: string;
  systemName: string;
  onContinue: (payload: { systemId: string; systemName: string }) => void;
  onRequestClose: () => void;
};

export default function AddConnectorApiStep({
  systemId,
  systemName,
  onContinue,
  onRequestClose,
}: AddConnectorApiStepProps) {
  const t = useTranslations("ConnectorAddApi");
  const setSystemStatus = useSetSystemStatus();
  const [hasExistingActions, setHasExistingActions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const forwardedRef = useRef(false);

  useEffect(() => {
    forwardedRef.current = false;
  }, [systemId]);

  useEffect(() => {
    const checkExistingData = async () => {
      setLoading(true);

      try {
        const flowState = getFlowState();

        if (flowState.previewActions && flowState.previewActions.length > 0) {
          setHasExistingActions(true);
          return;
        }

        if (flowState.isTemporary || isTemporarySystemId(systemId)) {
          setHasExistingActions(false);
          return;
        }

        const effectiveSystemId = flowState.systemId || systemId;
        if (effectiveSystemId && isPersistedSystemId(effectiveSystemId)) {
          const actions = await fetchActionsBySystemId(effectiveSystemId);
          if (actions.length > 0) {
            setHasExistingActions(true);
            updateFlowState({
              previewActions: actions,
              hasProcessedApi: true,
            });
          }
        }
      } catch (err) {
        console.error("Failed to check existing actions:", err);
      } finally {
        setLoading(false);
      }
    };

    void checkExistingData();
  }, [systemId]);

  useEffect(() => {
    if (!loading && hasExistingActions && !forwardedRef.current) {
      forwardedRef.current = true;
      onContinue({ systemId, systemName });
    }
  }, [hasExistingActions, loading, onContinue, systemId, systemName]);

  const handleSubmit = async (data: {
    sourceType: "file" | "url" | "text";
    content: string;
    selectedServerUrl?: string;
    fileType?: string;
  }) => {
    if (!systemId) return;

    setSubmitting(true);

    try {
      let effectiveSystemId = systemId;
      const flowState = getFlowState();

      const needsSystemCreation =
        isTemporarySystemId(systemId) ||
        flowState.isTemporary ||
        isTemporarySystemId(flowState.systemId || "");
      const systemAlreadyCreated = isPersistedSystemId(flowState.systemId || "");

      if (needsSystemCreation && !systemAlreadyCreated) {
        const connectorData = flowState.connectorData;
        if (!connectorData?.name) {
          throw new Error(t("errors.connectorNameRequiredGoBack"));
        }

        const system = await createSystem({
          name: connectorData.name,
          description: connectorData.description,
        });
        effectiveSystemId = system.system_id;

        updateFlowState({
          systemId: effectiveSystemId,
          creationSystemId: effectiveSystemId,
          systemName: system.name,
          isTemporary: false,
        });

        // A connector becomes available only after its authentication setup
        // succeeds in step 3. Keep a newly persisted step-2 connector safe if
        // the creation flow is later cancelled or interrupted.
        await setSystemStatus(effectiveSystemId, "inactive");
      } else if (systemAlreadyCreated) {
        effectiveSystemId = flowState.systemId!;
      }

      const result = await previewActionsFromContent({
        system_id: effectiveSystemId,
        source_type: data.sourceType,
        content: data.content,
        selected_server_url: data.selectedServerUrl,
        file_type: data.fileType,
      });

      updateFlowState({
        previewActions: result.actions || [],
        hasProcessedApi: true,
        systemName,
      });

      onContinue({
        systemId: effectiveSystemId,
        systemName,
      });
    } catch (err) {
      console.error("Preview failed:", err);

      const failedFlow = getFlowState();
      const provisionalSystemId = failedFlow.creationSystemId;
      if (
        provisionalSystemId &&
        failedFlow.systemId === provisionalSystemId &&
        !failedFlow.hasProcessedApi
      ) {
        try {
          await deleteSystem(provisionalSystemId);
          updateFlowState({
            systemId: `temp_${Date.now()}`,
            creationSystemId: null,
            isTemporary: true,
          });
        } catch (cleanupError) {
          console.error("Failed to remove incomplete connector:", cleanupError);
        }
      }

      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLoadingState
        className="min-h-[200px]"
        label={t("loading")}
        spinnerClassName="border-0 border-b-2 border-purple-600"
      />
    );
  }

  if (hasExistingActions) {
    return null;
  }

  return (
    <div className="mx-auto mt-8 flex min-h-0 w-full max-w-[1060px] flex-1 flex-col">
      <ActionUploadModal
        purpose="add-connector-modal-upload"
        onClose={onRequestClose}
        onSubmit={handleSubmit}
        submitLabel={t("actions.processApiSpec")}
        submitting={submitting}
        asModal={false}
        showHeader={true}
        headerVariant="connector-step"
      />
    </div>
  );
}
